import React, { useState } from 'react';
import classes from './RecipientsList.module.scss';
import recipientLoadingFailed from '../../../../../../assets/images/recipient-loading-failed.png';
import AppTable from '../../../../AppTable/AppTable';
import { WMButton, WMButtonVariant, WMSearchFilter } from '@walkme/wm-ui';
import Email from '../../../RecipientsListModal/Email';
import { ICCUser, UniqueId } from '@communication-center/data-objects';
import useSelector from '../../../../../infrastructure/useSelector';
import { useCalcRecipientsList } from './useCalcRecipientsList';
import { AudienceRuleGraphql } from '../../../../../../generated/apollo-hooks';
import RecipientsListEmptyState from './RecipientsListEmptyState';
import { useTranslation } from 'react-i18next';
import capitalize from 'lodash/capitalize';
import { EmptyListImage } from '../../../LazyLoadImages/EmptyListImage';

const keyPrefix = 'components.audiences.createAudienceDialog.recipientsData.recipientsList';

const columns = [
    {
        title: 'ID',
        key: 'email',
        dataKey: 'email',
        sortable: false,
        width: 250,
        minWidth: 60,
        flexGrow: 1,
        cellRenderer: ({ rowData }: { rowData: { id: UniqueId; label: ICCUser } }) => <Email rowData={rowData} />
    }
];

const dataTestId = 'calc-recipients-list';

const RecipientsList = ({ rules }: { rules: AudienceRuleGraphql[] }) => {
    const { t } = useTranslation('', { keyPrefix });
    const environmentId = useSelector(store => store.common.currentEnvironment?.id);

    const {
        query,
        isLoading,
        isLoadingMore,
        recipientsList,
        error,
        fetchNextPage,
        onSearch,
        tableRef,
        clientSideFiltered,
        retry
    } = useCalcRecipientsList({ rules });

    const EmptySearchJsx = () => (
        <RecipientsListEmptyState
            title={t('title')}
            description={t('description')}
            actionText={capitalize(t('actionText'))}
            image={EmptyListImage as any}
            onClick={() => onSearch('')}
        />
    );

    const emptyRenderer = () => {
        if (query?.search?.length > 0) {
            return <EmptySearchJsx />;
        }
        return null;
    };

    const [searchValue, setSearchValue] = useState('');
    const onSearchFn = (value: string) => {
        setSearchValue(value);
        onSearch(value);
    };

    return (
        <div className={classes.root}>
            <div className={classes.searchContainer}>
                <WMSearchFilter
                    className={classes.wmSearch}
                    classes={{ root: classes.wmSearchRoot }}
                    value={searchValue}
                    onSearch={onSearchFn}
                    placeholder={t('search')}
                    openByDefault
                    maxWidth="100%"
                    data-testid={`${dataTestId}-search`}
                />
            </div>

            <div className={classes.recipientsContainer}>
                {error ? (
                    <div className={classes.errorContainer}>
                        <img src={recipientLoadingFailed} />
                        <div className={classes.errorText}>Loading Failed.</div>
                        <WMButton className={classes.retry} variant={WMButtonVariant.Text} onClick={retry}>
                            {t('retry')}
                        </WMButton>
                    </div>
                ) : (
                    <AppTable
                        hideToolbar
                        tableName="recipient-list"
                        classNames={{ tableContainer: classes.tableContainer }}
                        columns={columns}
                        data={isLoading ? (clientSideFiltered?.length ? clientSideFiltered : undefined) : recipientsList}
                        environmentId={environmentId!}
                        loadingRows={Array(10).fill({ id: null })}
                        data-testid={`${dataTestId}-table`}
                        emptyTableRenderer={emptyRenderer}
                        wmTableProps={{
                            rowHeight: 40,
                            headerHeight: 40,
                            loadingMore: isLoadingMore || (isLoading && !!clientSideFiltered?.length),
                            onEndReachedThreshold: 300,
                            onEndReached: fetchNextPage,
                            ref: tableRef
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default RecipientsList;
